import { useNavigate } from 'react-router-dom'
import { Tenant } from '../api'
import { DataTable, ColumnDef } from '@/components/shared/DataTable/DataTable'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Building2, Eye, Layers } from 'lucide-react'

interface TenantTableProps {
  tenants: Tenant[]
  isLoading: boolean
}

export function TenantTable({ tenants, isLoading }: TenantTableProps) {
  const navigate = useNavigate()

  const getStatusBadge = (tenant: Tenant) => {
    if (tenant.isDeleted) {
      return <Badge variant="destructive">Deleted</Badge>
    }
    if (!tenant.isActive) {
      return <Badge variant="outline">Inactive</Badge>
    }
    return <Badge variant="default">Active</Badge>
  }

  // Define table columns
  const columns: ColumnDef<Tenant>[] = [
    {
      key: 'tenant',
      header: 'Tenant',
      cell: (tenant) => (
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted">
            <Layers className="h-4 w-4" />
          </div>
          <div>
            <div className="font-medium">{tenant.displayName}</div>
            <div className="text-sm text-muted-foreground">{tenant.name}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      cell: (tenant) => getStatusBadge(tenant),
    },
    {
      key: 'organizationCount',
      header: 'Orgs',
      headerClassName: 'text-center',
      className: 'text-center',
      cell: (tenant) => tenant.organizationCount || 0,
    },
    {
      key: 'userCount',
      header: 'Users',
      headerClassName: 'text-center',
      className: 'text-center',
      cell: (tenant) => tenant.userCount || 0,
    },
    {
      key: 'createdAt',
      header: 'Created',
      cell: (tenant) => (
        <div className="text-sm text-muted-foreground">
          {new Date(tenant.createdAt).toLocaleDateString()}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      headerClassName: 'text-right',
      className: 'text-right',
      cell: (tenant) => (
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/tenants/${tenant._id}`)}
          >
            <Eye className="mr-1 h-4 w-4" />
            Details
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/tenants/${tenant._id}`)}
          >
            <Building2 className="mr-1 h-4 w-4" />
            Organizations
          </Button>
        </div>
      ),
    },
  ]

  return (
    <DataTable
      columns={columns}
      data={tenants}
      isLoading={isLoading}
      emptyMessage="No tenants found"
      emptyIcon={<Layers className="h-12 w-12 text-muted-foreground" />}
      onRowClick={(tenant) => navigate(`/tenants/${tenant._id}`)}
    />
  )
}
